/// Reading a route out of a file another site exported.
///
/// GPX, KML and GeoJSON are all, underneath, an ordered list of coordinates -
/// so all three are parsed into the same thing and everything downstream stops
/// caring which it was. Supporting the set rather than one of them means
/// changing route planner does not mean changing this.
///
/// Nothing here touches the Maps API. Parsing and measuring a track needs no
/// key, no network and no billable call, which also makes it testable.

export interface LatLng {
  lat: number;
  lng: number;
}

export class ImportError extends Error {}

const EARTH_RADIUS_METRES = 6_371_008.8;

/// Great-circle distance. Deliberately not `geometry.spherical`: a file can be
/// read before - or without - the Maps script ever loading.
export function metresBetween(a: LatLng, b: LatLng): number {
  const toRad = (degrees: number) => (degrees * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_METRES * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function totalMetres(path: LatLng[]): number {
  let metres = 0;
  for (let i = 0; i < path.length - 1; i += 1) {
    const from = path[i];
    const to = path[i + 1];
    if (from && to) metres += metresBetween(from, to);
  }
  return metres;
}

const isFinitePair = (lat: number, lng: number) =>
  Number.isFinite(lat) && Number.isFinite(lng)
  && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;

/**
 * Drop points closer together than `minMetres` to the last one kept.
 *
 * Distance-based rather than keeping every nth point: a recorded track is
 * dense through towns and sparse along a straight road, so a fixed stride
 * would flatten exactly the corners that carry the shape while still
 * over-sampling the straights. The first and last points always survive, so
 * the route keeps its ends.
 */
export function decimate(path: LatLng[], minMetres: number): LatLng[] {
  if (minMetres <= 0 || path.length <= 2) return path;
  const first = path[0];
  const last = path[path.length - 1];
  if (!first || !last) return path;

  const kept: LatLng[] = [first];
  for (let i = 1; i < path.length - 1; i += 1) {
    const point = path[i];
    const previous = kept[kept.length - 1];
    if (point && previous && metresBetween(previous, point) >= minMetres) kept.push(point);
  }
  kept.push(last);
  return kept;
}

/// GPX puts latitude and longitude in attributes; KML and GeoJSON both order
/// them longitude-first. Getting that backwards puts a US route in China, so
/// each parser converts at its own boundary and everything after is `LatLng`.

function parseGpx(document: Document): LatLng[] {
  // Track points first, route points second: a file carrying both means a
  // recorded track and the plan it came from, and the track is the real line.
  const nodes = document.querySelectorAll('trkpt').length > 0
    ? document.querySelectorAll('trkpt')
    : document.querySelectorAll('rtept, wpt');

  const path: LatLng[] = [];
  nodes.forEach((node) => {
    const lat = Number(node.getAttribute('lat'));
    const lng = Number(node.getAttribute('lon'));
    if (isFinitePair(lat, lng)) path.push({ lat, lng });
  });
  return path;
}

function parseKml(document: Document): LatLng[] {
  const path: LatLng[] = [];
  // Every <coordinates> block in document order. A route split into several
  // LineStrings is one line as far as we are concerned.
  document.querySelectorAll('coordinates').forEach((node) => {
    for (const triple of (node.textContent ?? '').trim().split(/\s+/)) {
      const [lng, lat] = triple.split(',').map(Number);
      if (lng !== undefined && lat !== undefined && isFinitePair(lat, lng)) {
        path.push({ lat, lng });
      }
    }
  });
  return path;
}

function coordinatesFrom(geometry: unknown, into: LatLng[]): void {
  if (typeof geometry !== 'object' || geometry === null) return;
  const shape = geometry as Record<string, unknown>;

  if (shape.type === 'GeometryCollection' && Array.isArray(shape.geometries)) {
    for (const child of shape.geometries) coordinatesFrom(child, into);
    return;
  }

  const push = (pair: unknown) => {
    if (!Array.isArray(pair)) return;
    const [lng, lat] = pair as number[];
    if (lng !== undefined && lat !== undefined && isFinitePair(lat, lng)) {
      into.push({ lat, lng });
    }
  };

  if (shape.type === 'LineString' && Array.isArray(shape.coordinates)) {
    for (const pair of shape.coordinates) push(pair);
  } else if (shape.type === 'MultiLineString' && Array.isArray(shape.coordinates)) {
    for (const line of shape.coordinates) {
      if (Array.isArray(line)) for (const pair of line) push(pair);
    }
  } else if (shape.type === 'Point') {
    push(shape.coordinates);
  }
}

function parseGeoJson(text: string): LatLng[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new ImportError('That file is not valid GeoJSON.');
  }

  const path: LatLng[] = [];
  const walk = (node: unknown) => {
    if (typeof node !== 'object' || node === null) return;
    const shape = node as Record<string, unknown>;
    if (shape.type === 'FeatureCollection' && Array.isArray(shape.features)) {
      for (const feature of shape.features) walk(feature);
    } else if (shape.type === 'Feature') {
      coordinatesFrom(shape.geometry, path);
    } else {
      coordinatesFrom(shape, path);
    }
  };
  walk(parsed);
  return path;
}

/**
 * Read a route file into an ordered path.
 *
 * The format is chosen by extension where there is one and sniffed from the
 * content where there is not, because plenty of exports arrive named
 * `download` or `route.txt`.
 */
export function parseRouteFile(fileName: string, text: string): LatLng[] {
  const name = fileName.toLowerCase();
  const trimmed = text.trimStart();

  if (name.endsWith('.kmz')) {
    throw new ImportError(
      'KMZ is a zipped KML. Unzip it and import the .kml inside.',
    );
  }

  let path: LatLng[];
  if (name.endsWith('.geojson') || name.endsWith('.json') || trimmed.startsWith('{')) {
    path = parseGeoJson(text);
  } else {
    const document = new DOMParser().parseFromString(text, 'application/xml');
    if (document.querySelector('parsererror')) {
      throw new ImportError('That file could not be read as GPX, KML or GeoJSON.');
    }
    const isGpx = name.endsWith('.gpx') || document.querySelector('gpx, trkpt, rtept') !== null;
    path = isGpx ? parseGpx(document) : parseKml(document);
  }

  if (path.length < 2) {
    throw new ImportError('No route found in that file — it needs at least two points.');
  }
  return path;
}

/**
 * Decode a Google encoded polyline.
 *
 * Implemented here rather than borrowed from `geometry.encoding` so that
 * pasting a line needs no Maps script, no key and no billable load - and so
 * this file stays testable outside a browser.
 *
 * The format is a run of signed offsets, each in five-bit chunks, low chunk
 * first, with bit 0x20 marking "another chunk follows" and the sign carried in
 * the low bit of the assembled value.
 */
export function decodePolyline(encoded: string): LatLng[] {
  const path: LatLng[] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let byte: number;
    do {
      byte = encoded.charCodeAt(index) - 63;
      index += 1;
      if (Number.isNaN(byte) || byte < 0) throw new ImportError('That is not a valid polyline.');
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index <= encoded.length);
    lat += result & 1 ? ~(result >> 1) : result >> 1;

    result = 0;
    shift = 0;
    do {
      byte = encoded.charCodeAt(index) - 63;
      index += 1;
      if (Number.isNaN(byte) || byte < 0) throw new ImportError('That is not a valid polyline.');
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index <= encoded.length);
    lng += result & 1 ? ~(result >> 1) : result >> 1;

    const point = { lat: lat / 1e5, lng: lng / 1e5 };
    if (!isFinitePair(point.lat, point.lng)) {
      throw new ImportError('That polyline decodes to impossible coordinates.');
    }
    path.push(point);
  }
  return path;
}

/// Pull a path out of whatever a Directions response happens to be shaped
/// like. The overview is preferred; per-step geometry is the fallback for a
/// response that carries legs but no overview.
function fromDirections(payload: Record<string, unknown>): LatLng[] {
  const routes = payload.routes;
  if (!Array.isArray(routes) || routes.length === 0) return [];
  const route = routes[0] as Record<string, unknown>;

  const overview = route.overview_polyline;
  if (typeof overview === 'string') return decodePolyline(overview);
  if (typeof overview === 'object' && overview !== null) {
    const points = (overview as Record<string, unknown>).points;
    if (typeof points === 'string') return decodePolyline(points);
  }

  const path: LatLng[] = [];
  for (const leg of (route.legs as Record<string, unknown>[]) ?? []) {
    for (const step of (leg.steps as Record<string, unknown>[]) ?? []) {
      const line = step.polyline as Record<string, unknown> | undefined;
      if (line && typeof line.points === 'string') path.push(...decodePolyline(line.points));
    }
  }
  return path;
}

/// A bare list of coordinates, one pair per line. Read as `lat,lng` - the
/// order Google Maps itself shows when you copy a dropped pin, and the order
/// anyone typing a pair by hand means. KML and GeoJSON keep their own
/// longitude-first order inside their own parsers.
function fromCoordinateList(text: string): LatLng[] {
  const path: LatLng[] = [];
  for (const line of text.split(/[\n;]+/)) {
    const trimmed = line.trim();
    if (trimmed === '') continue;
    const [lat, lng] = trimmed.split(/[,\s]+/).map(Number);
    if (lat === undefined || lng === undefined || !isFinitePair(lat, lng)) return [];
    path.push({ lat, lng });
  }
  return path;
}

/**
 * Read a route out of pasted text, for whatever came back off another site's
 * network traffic: a Directions response, a GeoJSON blob, a bare list of
 * coordinates, or a lone encoded polyline.
 *
 * The polyline case is last because it is the only one with no structure to
 * recognise - anything that is not JSON, not XML and not a list of numbers is
 * assumed to be one, and says so plainly when the decode fails.
 */
export function parsePastedRoute(text: string): LatLng[] {
  const trimmed = text.trim();
  if (trimmed === '') throw new ImportError('Nothing pasted.');

  let path: LatLng[] = [];

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      throw new ImportError('That looks like JSON but could not be parsed.');
    }
    if (typeof parsed === 'object' && parsed !== null && 'routes' in parsed) {
      path = fromDirections(parsed as Record<string, unknown>);
    }
    if (path.length === 0) path = parseGeoJson(trimmed);
    if (path.length === 0 && Array.isArray(parsed)) {
      for (const item of parsed) {
        if (Array.isArray(item)) {
          const [lat, lng] = item as number[];
          if (lat !== undefined && lng !== undefined && isFinitePair(lat, lng)) {
            path.push({ lat, lng });
          }
        } else if (typeof item === 'object' && item !== null) {
          const point = item as Record<string, unknown>;
          const lat = Number(point.lat ?? point.latitude);
          const lng = Number(point.lng ?? point.lon ?? point.longitude);
          if (isFinitePair(lat, lng)) path.push({ lat, lng });
        }
      }
    }
  } else if (trimmed.startsWith('<')) {
    path = parseRouteFile('pasted.xml', trimmed);
  } else if (/^-?\d+(\.\d+)?\s*[,\s]\s*-?\d+(\.\d+)?/.test(trimmed)) {
    path = fromCoordinateList(trimmed);
  } else {
    path = decodePolyline(trimmed);
  }

  if (path.length < 2) {
    throw new ImportError('No route found in that — it needs at least two points.');
  }
  return path;
}

/// A route lifted out of a Google Maps directions link.
export interface GoogleDirectionsLink {
  /// Every point the line passes through, in order: the named stops and any
  /// points that were dragged in to shape it between them.
  stops: LatLng[];
  /// The stop names from the URL path. Fewer than `stops` whenever the link
  /// carries dragged points, which have no name.
  names: string[];
  travelMode: 'DRIVING' | 'WALKING';
}

const MAPS_DIRECTIONS = /google\.[a-z.]+\/maps\/dir\//i;

export const looksLikeGoogleMapsLink = (text: string) => MAPS_DIRECTIONS.test(text.trim());

/**
 * Read a Google Maps directions URL.
 *
 * Two sources in one link, and the useful one is not the obvious one. The
 * readable path segments carry the stop *names*; the `data=` blob carries
 * every point the line actually goes through, including the ones dragged in
 * between stops to shape it. Rebuilding from names alone would quietly lose
 * that shaping and produce a different road.
 *
 * Inside `data`, each point appears as `!1d<longitude>!2d<latitude>` — in that
 * order, longitude first, which is the reverse of how they are written
 * everywhere else in this file and the easiest thing in it to get backwards.
 * They appear in route order, so reading them straight through is the route.
 */
export function parseGoogleMapsDirections(url: string): GoogleDirectionsLink {
  const trimmed = url.trim();
  if (!looksLikeGoogleMapsLink(trimmed)) {
    throw new ImportError('That is not a Google Maps directions link.');
  }

  const stops: LatLng[] = [];
  const data = trimmed.split('/data=')[1] ?? '';
  const pattern = /!1d(-?\d+(?:\.\d+)?)!2d(-?\d+(?:\.\d+)?)/g;
  let match = pattern.exec(data);
  while (match !== null) {
    const lng = Number(match[1]);
    const lat = Number(match[2]);
    if (isFinitePair(lat, lng)) stops.push({ lat, lng });
    match = pattern.exec(data);
  }

  // The path between /dir/ and the @centre. Used for labels, and as the whole
  // answer for a short link that carries no data blob.
  const path = trimmed.split('/maps/dir/')[1] ?? '';
  const names = path
    .split(/[/?]/)
    .slice(0, path.includes('/@') ? path.split('/').findIndex((part) => part.startsWith('@')) : undefined)
    .map((part) => decodeURIComponent(part.replace(/\+/g, ' ')).trim())
    .filter((part) => part !== '' && !part.startsWith('@') && !part.startsWith('data='));

  if (stops.length < 2) {
    throw new ImportError(
      names.length >= 2
        ? 'That link names its stops but carries no coordinates. Paste them as a list instead.'
        : 'No route found in that link.',
    );
  }

  // !3e0 driving, !3e1 cycling, !3e2 walking, !3e3 transit.
  const mode = /!3e(\d)/.exec(data)?.[1];
  return { stops, names, travelMode: mode === '2' ? 'WALKING' : 'DRIVING' };
}
